<template>
    <div class="content">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">
                        {{ $t("agent_commission.menu.round_detail") }}
                        <span v-if="round"> — {{ round.code }}</span>
                    </h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-bread-crumb></route-bread-crumb>
                    </nav>
                </div>
                <div class="col-lg-6 col-5 text-right">
                    <base-button type="secondary" size="sm" @click="$router.push('/backoffice/agent-commission/rounds')">
                        <i class="fa fa-arrow-left mr-1"></i>{{ $t("back") }}
                    </base-button>
                    <base-button v-if="round" type="danger" size="sm" @click="confirmDelete">
                        {{ $t("agent_commission.round.delete") }}
                    </base-button>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">

            <!-- Round header summary -->
            <card v-if="round" class="mb-4">
                <div class="row">
                    <div class="col-md-3">
                        <small class="text-muted d-block">{{ $t("agent_commission.round.code") }}</small>
                        <strong>{{ round.code }}</strong>
                    </div>
                    <div class="col-md-3">
                        <small class="text-muted d-block">{{ $t("agent_commission.round.period") }}</small>
                        <strong>{{ formatDate(round.period_start) }} — {{ formatDate(round.period_end) }}</strong>
                    </div>
                    <div class="col-md-3">
                        <small class="text-muted d-block">{{ $t("agent_commission.round.total_sales") }}</small>
                        <strong>{{ formatAmount(round.total_sales) }}</strong>
                    </div>
                    <div class="col-md-3">
                        <small class="text-muted d-block">{{ $t("agent_commission.round.total_commission") }}</small>
                        <strong>{{ formatAmount(round.total_commission) }}</strong>
                    </div>
                </div>
                <div class="row mt-2">
                    <div class="col-md-3">
                        <small class="text-muted d-block">{{ $t("agent_commission.round.agent_count") }}</small>
                        <strong>{{ round.agent_count }}</strong>
                    </div>
                    <div class="col-md-3">
                        <small class="text-muted d-block">{{ $t("agent_commission.round.order_count") }}</small>
                        <strong>{{ round.order_count }}</strong>
                    </div>
                    <div class="col-md-3">
                        <small class="text-muted d-block">{{ $t("agent_commission.round.status") }}</small>
                        <span class="badge badge-success">{{ $t("agent_commission.round.status_closed") }}</span>
                    </div>
                    <div class="col-md-3">
                        <small class="text-muted d-block">{{ $t("agent_commission.round.created_at") }}</small>
                        <strong>{{ formatDate(round.created_at) }}</strong>
                    </div>
                </div>
            </card>

            <!-- Per-agent table -->
            <card>
                <template slot="header">
                    <h3 class="mb-0">{{ $t("agent_commission.agent.agent_name") }}</h3>
                </template>

                <div v-if="!round || !round.agents || round.agents.length === 0" class="text-center text-muted py-4">
                    {{ $t("agent_commission.empty_agents") }}
                </div>

                <el-table
                    v-else
                    :data="round.agents"
                    header-row-class-name="thead-light"
                    @expand-change="onExpandAgent"
                >
                    <el-table-column type="expand">
                        <div slot-scope="{ row }">
                            <div v-if="agentOrdersLoading[row.bo_order_user_id]" class="text-center py-3">
                                <i class="fa fa-spinner fa-spin"></i>
                            </div>
                            <div v-else-if="!agentOrders[row.bo_order_user_id] || agentOrders[row.bo_order_user_id].length === 0" class="text-muted px-3">
                                {{ $t("agent_commission.empty_orders") }}
                            </div>
                            <el-table
                                v-else
                                :data="agentOrders[row.bo_order_user_id]"
                                header-row-class-name="thead-light"
                                size="small"
                                class="mx-3"
                            >
                                <el-table-column :label="$t('agent_commission.order.order_number')">
                                    <div slot-scope="{ row: orderRow }">{{ orderRow.platform_order_id || ('#' + orderRow.id) }}</div>
                                </el-table-column>
                                <el-table-column :label="$t('agent_commission.order.pay_amount')">
                                    <div slot-scope="{ row: orderRow }">{{ formatAmount(orderRow.pay_amount) }}</div>
                                </el-table-column>
                                <el-table-column :label="$t('agent_commission.order.payment_type')">
                                    <div slot-scope="{ row: orderRow }">
                                        <span class="badge" :class="orderRow.payment_method_type === 2 ? 'badge-warning' : 'badge-info'">
                                            {{ paymentTypeLabel(orderRow.payment_method_type) }}
                                        </span>
                                    </div>
                                </el-table-column>
                                <el-table-column :label="$t('agent_commission.order.completed_at')">
                                    <div slot-scope="{ row: orderRow }">{{ formatDate(orderRow.completed_at) }}</div>
                                </el-table-column>
                                <el-table-column :label="$t('agent_commission.order.created_at')">
                                    <div slot-scope="{ row: orderRow }">{{ formatDate(orderRow.created_at) }}</div>
                                </el-table-column>
                                <el-table-column :label="$t('agent_commission.order.view_order')" align="right">
                                    <div slot-scope="{ row: orderRow }">
                                        <a :href="`/backoffice/orders/${orderRow.id}`" target="_blank" class="btn btn-sm btn-info">
                                            <i class="fa fa-external-link-alt"></i>
                                        </a>
                                    </div>
                                </el-table-column>
                            </el-table>
                        </div>
                    </el-table-column>

                    <el-table-column :label="$t('agent_commission.agent.agent_name')" prop="agent_name"></el-table-column>
                    <el-table-column :label="$t('agent_commission.agent_profile')">
                        <div slot-scope="{ row }">{{ row.profile_name || $t('agent_commission.use_default') }}</div>
                    </el-table-column>
                    <el-table-column :label="$t('agent_commission.agent.total_sales')">
                        <div slot-scope="{ row }">{{ formatAmount(row.total_sales) }}</div>
                    </el-table-column>
                    <el-table-column :label="$t('agent_commission.agent.tier_percent')">
                        <div slot-scope="{ row }">{{ row.tier_percent }}%</div>
                    </el-table-column>
                    <el-table-column :label="$t('agent_commission.agent.commission_amount')">
                        <div slot-scope="{ row }">{{ formatAmount(row.commission_amount) }}</div>
                    </el-table-column>
                    <el-table-column :label="$t('agent_commission.agent.order_count')" prop="order_count"></el-table-column>
                </el-table>
            </card>
        </div>
    </div>
</template>

<script>
import { Table, TableColumn } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { mapGetters, mapActions } from "vuex";
import swal from "sweetalert2";
import Vue from "vue";

export default {
    name: "AgentCommissionRoundDetail",
    components: {
        RouteBreadCrumb,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
    },
    data() {
        return {
            agentOrders: {},
            agentOrdersLoading: {},
        };
    },
    computed: {
        ...mapGetters({
            round: "AgentCommission/getCurrentRound",
        }),
        roundId() {
            return this.$route.params.id;
        },
    },
    mounted() {
        this.loadRound();
    },
    methods: {
        ...mapActions({
            fetchRound: "AgentCommission/fetchRound",
            fetchAgentOrders: "AgentCommission/fetchAgentOrders",
            deleteRound: "AgentCommission/deleteRound",
        }),
        async loadRound() {
            try {
                await this.fetchRound(this.roundId);
            } catch (err) {
                swal.fire({ title: err.message, icon: "error", confirmButtonClass: "btn btn-danger btn-fill", buttonsStyling: false });
            }
        },
        async onExpandAgent(row, expandedRows) {
            const userId = row.bo_order_user_id;
            if (this.agentOrders[userId] !== undefined) return; // already loaded
            Vue.set(this.agentOrdersLoading, userId, true);
            try {
                const resp = await this.fetchAgentOrders({ id: this.roundId, userId });
                Vue.set(this.agentOrders, userId, resp.data.Data || []);
            } catch (err) {
                Vue.set(this.agentOrders, userId, []);
            } finally {
                Vue.set(this.agentOrdersLoading, userId, false);
            }
        },
        confirmDelete() {
            swal.fire({
                title: this.$t("agent_commission.round.confirm_delete"),
                text: this.$t("agent_commission.round.confirm_delete_msg"),
                icon: "warning",
                showCancelButton: true,
                confirmButtonClass: "btn btn-danger btn-fill",
                cancelButtonClass: "btn btn-secondary btn-fill",
                confirmButtonText: this.$t("confirm"),
                cancelButtonText: this.$t("cancel"),
                buttonsStyling: false,
            }).then(async result => {
                if (result.value) {
                    try {
                        await this.deleteRound(this.roundId);
                        swal.fire({
                            title: this.$t("agent_commission.delete_success"),
                            icon: "success",
                            confirmButtonClass: "btn btn-success btn-fill",
                            buttonsStyling: false,
                        });
                        this.$router.push("/backoffice/agent-commission/rounds");
                    } catch (err) {
                        const msg = err.response && err.response.data && err.response.data.Cause
                            ? err.response.data.Cause
                            : err.message;
                        swal.fire({ title: msg, icon: "error", confirmButtonClass: "btn btn-danger btn-fill", buttonsStyling: false });
                    }
                }
            });
        },
        paymentTypeLabel(type) {
            return Number(type) === 2
                ? this.$t("agent_commission.payment_type_cod")
                : this.$t("agent_commission.payment_type_transfer");
        },
        formatDate(val) {
            if (!val) return "-";
            return val.replace("T", " ").substring(0, 16);
        },
        formatAmount(val) {
            if (val == null) return "-";
            return Number(val).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
    },
};
</script>
